import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
// import { useUser } from '../contexts/UserContext'; // Not currently used
// import { portfolioApi } from '../services/api'; // Not currently used
import LoadingSpinner from '../components/UI/LoadingSpinner';
import BankLinking from '../components/BankLinking';
import BankTransfer from '../components/BankTransfer';

interface WalletTransaction {
  id: string;
  type: 'dividend' | 'investment' | 'withdrawal' | 'fee';
  amount: number;
  date: string;
  propertyName?: string;
  description: string;
  status: 'completed' | 'pending' | 'failed';
}

interface WalletSummary {
  totalBalance: number;
  availableBalance: number;
  pendingBalance: number;
  totalDividends: number;
  thisMonthDividends: number;
  totalInvested: number;
  totalWithdrawn: number;
}

const Wallet: React.FC = () => {
  // const { user } = useUser(); // Not currently used in this component
  const [summary, setSummary] = useState<WalletSummary | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'dividends' | 'banking'>('overview');

  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    try {
      setIsLoading(true);
      // Mock data for now - replace with actual API calls
      setSummary({
        totalBalance: 15750.00,
        availableBalance: 15250.00,
        pendingBalance: 500.00,
        totalDividends: 1250.00,
        thisMonthDividends: 125.00,
        totalInvested: 25000.00,
        totalWithdrawn: 500.00,
      });

      setTransactions([
        {
          id: '1',
          type: 'dividend',
          amount: 125.00,
          date: '2025-09-01T10:00:00Z',
          propertyName: 'The Cyrus',
          description: 'Monthly dividend payment',
          status: 'completed'
        },
        {
          id: '2',
          type: 'investment',
          amount: -5000.00,
          date: '2025-08-15T14:30:00Z',
          propertyName: 'The Cyrus',
          description: 'Property investment',
          status: 'completed'
        },
        {
          id: '3',
          type: 'dividend',
          amount: 98.50,
          date: '2025-08-01T10:00:00Z',
          propertyName: 'Private Credit Fund',
          description: 'Monthly dividend payment',
          status: 'completed'
        }
      ]);
    } catch (error) {
      console.error('Failed to load wallet data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'dividend':
        return (
          <div className="p-2 bg-green-500 bg-opacity-20 rounded-full">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
        );
      case 'investment':
        return (
          <div className="p-2 bg-blue-500 bg-opacity-20 rounded-full">
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        );
      case 'withdrawal':
        return (
          <div className="p-2 bg-orange-500 bg-opacity-20 rounded-full">
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="p-2 bg-neutral-500 bg-opacity-20 rounded-full">
            <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900">
      {/* Header */}
      <div className="bg-neutral-800 border-b border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-neutral-100">Wallet</h1>
            <p className="mt-2 text-neutral-400">Track your investment finances and dividend earnings</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Wallet Summary */}
        {summary && (
          <>
            {/* Main Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-5 sm:p-8 mb-8 bg-gradient-to-r from-honey-500 to-honey-600 border-0"
            >
              <div className="text-center">
                <p className="text-sm font-medium text-honey-100 opacity-90">Total Balance</p>
                <p className="text-4xl font-bold text-white mt-2">{formatCurrency(summary.totalBalance)}</p>
                <div className="flex items-center justify-center space-x-6 mt-6">
                  <div className="text-center">
                    <p className="text-xs text-honey-100 opacity-75">Available</p>
                    <p className="text-lg font-semibold text-white">{formatCurrency(summary.availableBalance)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-honey-100 opacity-75">Pending</p>
                    <p className="text-lg font-semibold text-white">{formatCurrency(summary.pendingBalance)}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Total Dividends</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.totalDividends)}</p>
                  </div>
                  <div className="p-3 bg-green-500 bg-opacity-20 rounded-full">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">This Month</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(summary.thisMonthDividends)}</p>
                  </div>
                  <div className="p-3 bg-green-500 bg-opacity-20 rounded-full">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Total Invested</p>
                    <p className="text-2xl font-bold text-blue-400">{formatCurrency(summary.totalInvested)}</p>
                  </div>
                  <div className="p-3 bg-blue-500 bg-opacity-20 rounded-full">
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="card p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-400">Total Withdrawn</p>
                    <p className="text-2xl font-bold text-orange-400">{formatCurrency(summary.totalWithdrawn)}</p>
                  </div>
                  <div className="p-3 bg-orange-500 bg-opacity-20 rounded-full">
                    <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'transactions', label: 'Transactions' },
              { key: 'dividends', label: 'Dividends' },
              { key: 'banking', label: 'Banking' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.key
                    ? 'border-honey-500 text-honey-500'
                    : 'border-transparent text-neutral-400 hover:text-neutral-300 hover:border-neutral-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Transactions List */}
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-neutral-100 mb-4">Recent Transactions</h3>
              <div className="space-y-4">
                {transactions.map((transaction, index) => (
                  <motion.div
                    key={transaction.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-4 bg-neutral-700 rounded-lg"
                  >
                    <div className="flex items-center space-x-4">
                      {getTransactionIcon(transaction.type)}
                      <div>
                        <p className="font-medium text-neutral-100">{transaction.description}</p>
                        {transaction.propertyName && (
                          <p className="text-sm text-neutral-400">{transaction.propertyName}</p>
                        )}
                        <p className="text-xs text-neutral-500">{formatDate(transaction.date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${
                        transaction.amount >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {transaction.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(transaction.amount))}
                      </p>
                      <p className={`text-xs ${
                        transaction.status === 'completed' ? 'text-green-400' : 
                        transaction.status === 'pending' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {transaction.status}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="card p-6 cursor-pointer hover:bg-neutral-750 transition-colors"
              onClick={() => setActiveTab('banking')}
            >
              <div className="text-center">
                <div className="p-4 bg-honey-500 bg-opacity-20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-honey-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-100 mb-2">Add Funds</h3>
                <p className="text-sm text-neutral-400">Deposit money from your bank account</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="card p-6 cursor-pointer hover:bg-neutral-750 transition-colors"
              onClick={() => setActiveTab('banking')}
            >
              <div className="text-center">
                <div className="p-4 bg-green-500 bg-opacity-20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-100 mb-2">Link Bank</h3>
                <p className="text-sm text-neutral-400">Connect your bank account securely</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="card p-6 cursor-pointer hover:bg-neutral-750 transition-colors"
              onClick={() => setActiveTab('banking')}
            >
              <div className="text-center">
                <div className="p-4 bg-orange-500 bg-opacity-20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-100 mb-2">Withdraw</h3>
                <p className="text-sm text-neutral-400">Transfer funds to your bank account</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="card p-6 cursor-pointer hover:bg-neutral-750 transition-colors"
            >
              <div className="text-center">
                <div className="p-4 bg-blue-500 bg-opacity-20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-neutral-100 mb-2">Statements</h3>
                <p className="text-sm text-neutral-400">Download monthly statements</p>
              </div>
            </motion.div>
          </div>
        )}

        {/* Banking Tab */}
        {activeTab === 'banking' && (
          <div className="space-y-8">
            {/* Banking Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Bank Linking Section */}
              <div className="card p-6">
                <BankLinking onSuccess={() => {
                  // Refresh wallet data if needed
                  loadWalletData();
                }} />
              </div>

              {/* Transfer Section */}
              <div className="space-y-6">
                {/* Deposit */}
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-neutral-100 mb-4">Deposit Funds</h4>
                  <BankTransfer
                    type="deposit"
                    onSuccess={() => {
                      loadWalletData();
                      toast.success('Deposit initiated successfully!');
                    }}
                  />
                </div>

                {/* Withdraw */}
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-neutral-100 mb-4">Withdraw Funds</h4>
                  <BankTransfer
                    type="withdraw"
                    onSuccess={() => {
                      loadWalletData();
                      toast.success('Withdrawal initiated successfully!');
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
